import React, { useEffect, useMemo, useState } from "react";
import { Tabs, Typography, Select, Space, Table, Row, Col, Button, Drawer, Card, notification } from "antd";
import { DeleteOutlined, AreaChartOutlined, ShopOutlined } from "@ant-design/icons";

//
import Meta from "../Meta";
import SectionHeader from "../GetStarted/SectionHeader";
import PermissionPopover from "../PermissionPopover";
import ShopSelect from "../ShopSelect";

//
import log from "../../utils/log";
import metaData from "../../data/metaData";
import permissionMetaData from "../../data/permission";
import deepcopy from "deepcopy";
import c from "../../utils/common";

//
import useShop from "../../hooks/useShop";
import useCompanyStore from "../../states/useCompany";
import useShopStore from "../../states/useShop";
import Checkbox from "../General/Checkbox";
import useAuthStore from "../../states/useAuth";
import ShopLogo from "../Shop/Logo";

//
const { Title, Text } = Typography;

const uncheckedRoles = {
  marketing: false,
  shopManager: false,
  shopStaff: false,
};

const ShopPermissionDrawer = ({
  isShowShopPermissionDrawer,
  selectedItems,
  tableData,
  setIsShowShopSelectionDrawer,
  onClose,
  onConfirm,
}) => {
  const [roles, setRoles] = useState(uncheckedRoles);
  const { company } = useCompanyStore();

  const handleCheckboxChange = (key) => {
    // const checked = e.target.checked;

    if (key === "marketing") {
      setRoles({
        marketing: !roles.marketing,
        shopManager: false,
        shopStaff: false,
      });
    } else if (key === "shopManager") {
      setRoles({
        marketing: false,
        shopManager: !roles.shopManager,
        shopStaff: false,
      });
    } else {
      setRoles({
        marketing: false,
        shopManager: false,
        shopStaff: !roles.shopStaff,
      });
    }
  };

  const closeDrawer = () => {
    //reset roles
    setRoles({
      marketing: false,
      shopManager: false,
      shopStaff: false,
    });
    onClose();
  };

  return (
    <Drawer
      placement="right"
      onClose={closeDrawer}
      open={isShowShopPermissionDrawer}
      width={"55%"}
      // closable={false}
      footer={
        roles.shopManager || roles.shopStaff ? (
          <Row justify="center">
            {selectedItems.length > 0 ? (
              <Title level={5} style={{ margin: "0.5rem 0 1rem 0" }}>{`${selectedItems.length} shops selected`}</Title>
            ) : null}
            <Button
              color="primary"
              size="large"
              variant="solid"
              style={{ width: "100%" }}
              onClick={(e) => {
                e.preventDefault();
                setIsShowShopSelectionDrawer(true);
              }}
            >
              Select Shops
            </Button>
          </Row>
        ) : null
      }
      extra={
        <Space>
          <Button size="large" variant="outline" onClick={closeDrawer}>
            Cancel
          </Button>
          <Button
            size="large"
            type="primary"
            disabled={
              (!roles.marketing && !(roles.shopManager || roles.shopStaff)) ||
              ((roles.shopManager || roles.shopStaff) && selectedItems.length === 0)
            }
            onClick={() => {
              let result = deepcopy(roles);

              //reset radio btn
              setRoles({
                marketing: false,
                shopManager: false,
                shopStaff: false,
              });

              onConfirm(result);
            }}
          >
            Apply
          </Button>
        </Space>
      }
    >
      <SectionHeader title={"Select permission group"} />

      {tableData.some((e) => e.id == "all") ? null : (
        <Card
          bordered
          style={
            roles.marketing
              ? { border: "1px solid var(--ion-color-primary)", cursor: "pointer", marginBottom: "1rem" }
              : { cursor: "pointer", marginBottom: "1rem" }
          }
          onClick={() => {
            handleCheckboxChange("marketing");
          }}
        >
          <Row>
            <Col span={22}>
              <SectionHeader
                title={
                  <>
                    <AreaChartOutlined /> Marketing
                  </>
                }
                description="Manage all shops"
              />
            </Col>
            <Col span={2}>
              <Checkbox checked={roles.marketing} />
            </Col>
          </Row>

          <Space style={{ marginBottom: "1rem" }}>
            <PermissionPopover id="report" />
            <PermissionPopover id="shop" />
            <PermissionPopover id="product" />
            <PermissionPopover id="eStamp" />
            {company?.permission?.isTakeawayEnabled ? <PermissionPopover id="takeaway" /> : null}
            <PermissionPopover id="promotionCode" />
            <PermissionPopover id="welcome" />
            <PermissionPopover id="ad" />
            <PermissionPopover id="pushNotification" />
          </Space>
        </Card>
      )}

      <Card
        bordered
        style={
          roles.shopManager
            ? {
                border: "1px solid var(--ion-color-primary)",
                cursor: "pointer",
                marginBottom: "1rem",
              }
            : { cursor: "pointer", marginBottom: "1rem" }
        }
        onClick={() => {
          // Manually toggle the checked state and pass it to handleCheckboxChange
          handleCheckboxChange("shopManager");
        }}
      >
        <Row>
          <Col span={22}>
            <SectionHeader
              title={
                <>
                  <ShopOutlined /> Shop Manager
                </>
              }
              description="View reports for selected shops"
            />
          </Col>
          <Col span={2}>
            <Checkbox checked={roles.shopManager} />
          </Col>
        </Row>

        <Space>
          <PermissionPopover id="report" />
          {company?.permission?.isTakeawayEnabled ? <PermissionPopover id="takeaway" /> : null}
          <PermissionPopover id="redeem" />
          <PermissionPopover id="eStampQR" />
          {company?.permission?.isTakeawayEnabled ? <PermissionPopover id="takeawayQR" /> : null}
        </Space>
      </Card>

      <Card
        bordered
        style={
          roles.shopStaff
            ? {
                border: "1px solid var(--ion-color-primary)",
                cursor: "pointer",
                marginBottom: "1rem",
              }
            : { cursor: "pointer", marginBottom: "1rem" }
        }
        onClick={() => {
          handleCheckboxChange("shopStaff");
        }}
      >
        <Row>
          <Col span={22}>
            <SectionHeader
              title={
                <>
                  <ShopOutlined /> Shop Staff
                </>
              }
              description="Scan QR code for selected shops"
            />
          </Col>

          <Col span={2}>
            <Checkbox checked={roles.shopStaff} />
          </Col>
        </Row>

        <Space>
          <PermissionPopover id="redeem" />
          <PermissionPopover id="eStampQR" />
          {company?.permission?.isTakeawayEnabled ? <PermissionPopover id="takeawayQR" /> : null}
        </Space>
      </Card>
    </Drawer>
  );
};

const ShopSelectDrawer = ({ isShowShopSelectionDrawer, selectedItems, options, onClose, onConfirm }) => {
  // log.debug("options", options);

  return (
    <Drawer
      placement="right"
      onClose={onClose}
      open={isShowShopSelectionDrawer}
      width={"45%"}
      // closable={false}
    >
      <Row>
        <SectionHeader title={"Select Shops"} />

        <Row align="middle" style={{ width: "100%" }}>
          <Col span={20}>
            <ShopSelect
              selectedItems={selectedItems}
              onConfirm={onConfirm}
              options={options}
              placeholder="Search shop name"
            />
          </Col>

          <Col span={4} style={{ textAlign: "end" }}>
            <Button
              size="large"
              type="primary"
              disabled={selectedItems.length == 0}
              style={{ marginLeft: "1rem" }}
              onClick={() => {
                //log.debug(selectedItems);
                onClose();
              }}
            >
              Next
            </Button>
          </Col>
        </Row>
      </Row>

      <Row style={{ marginBottom: "5rem" }}></Row>
    </Drawer>
  );
};

const UserPermissionSelector = ({ permission = permissionMetaData.default.no, readOnly, onChange = () => {} }) => {
  const { getShops } = useShop();
  const { user } = useAuthStore();
  const { shopsCache } = useShopStore();
  const { company } = useCompanyStore();

  const convertPermissionToArray = (permission, shops = []) => {
    let { shop, takeaway, report, product, estamp, promotionCode, welcome, ad, pushNotification } = permission;

    let roles = [];

    // Determine shopStaff shops (any shopId in redeem, distribute, or estamp.distribute)
    const shopStaffShops = [...new Set([...product.redeem, ...product.distribute, ...estamp.distribute])];

    // Determine shopManager shops (must also satisfy report and takeaway)
    const shopManagerShops = shopStaffShops.filter((shopId) => report.includes(shopId) && takeaway.includes(shopId));

    // Check if all permissions apply for marketing
    if (shop && takeaway?.[0] === "all" && report?.[0] === "all" && product.issue && estamp.issue && promotionCode && welcome && ad && pushNotification) {
      roles.push({ role: "marketing", shopIds: ["all"] });
    }

    // Include shopManager roles if applicable
    if (shopManagerShops.length > 0) {
      roles.push({ role: "shopManager", shopIds: shopManagerShops });
    }

    // Filter out shopStaffShops that are already included in shopManagerShops
    const filteredShopStaffShops = shopStaffShops.filter((shopId) => !shopManagerShops.includes(shopId));

    // Include shopStaff roles if applicable
    if (filteredShopStaffShops.length > 0) {
      roles.push({ role: "shopStaff", shopIds: filteredShopStaffShops });
    }

    // Generate table data with roles and permissions
    let tableData = [];
    for (let e of roles) {
      let { role, shopIds } = e;

      // If role is marketing, push it as "all"
      if (role === "marketing") {
        tableData.push({
          id: "all",
          name: "All Shops",
          address: undefined,
          key: "all",
          roles: ["marketing"],
          permissions: {}, // No permissions shown for "all"
        });
      } else {
        // If role is shopManager or shopStaff, push each shop entry with its specific permissions
        shopIds.forEach((shopId) => {
          const shop = shops.find((s) => s.id === shopId);
          if (shop) {
            // Collect permissions for each shop
            const permissions = {};
            if (takeaway.includes(shopId) && takeaway[0] !== "all") {
              permissions.takeaway = takeaway.filter((id) => id !== "all");
            }
            if (report.includes(shopId) && report[0] !== "all") {
              permissions.report = report.filter((id) => id !== "all");
            }
            if (product.redeem.includes(shopId) || product.distribute.includes(shopId)) {
              permissions.product = {
                redeem: product.redeem.filter((id) => id !== "all"),
                distribute: product.distribute.filter((id) => id !== "all"),
              };
            }
            if (estamp.distribute.includes(shopId)) {
              permissions.estamp = {
                distribute: estamp.distribute.filter((id) => id !== "all"),
              };
            }

            // Only show permissions if there are specific shops (i.e., not "all")
            tableData.push({
              id: shop.id,
              name: shop.name,
              address: shop.address,
              key: shop.id,
              roles: [role], // Assign the appropriate role
              permissions: permissions,
            });
          }
        });
      }
    }

    log.debug("Generated tableData", tableData);
    return tableData;
  };

  const convertArrayToPermission = (shopArray) => {
    const updatedPermission = deepcopy(permissionMetaData.default.no);
    //log.debug("arr before toPermission", shopArray);

    shopArray.forEach((shop) => {
      log.debug(shop?.roles);

      if (shop.id === "all") {
        // Update the "all" shop-related permissions
        updatedPermission.shop = true;
        updatedPermission.takeaway = ["all"]; // Set to ["all"]
        updatedPermission.report = ["all"]; // Set to ["all"]
        updatedPermission.product.issue = true;
        updatedPermission.estamp.issue = true;
        updatedPermission.promotionCode = true;
        updatedPermission.welcome = true;
        updatedPermission.ad = true;
        updatedPermission.pushNotification = true;
      } else {
        // Handle permissions for specific shops
        if (shop.roles?.includes("shopManager")) {
          updatedPermission.product.redeem.push(shop.id);
          updatedPermission.product.distribute.push(shop.id);
          updatedPermission.estamp.distribute.push(shop.id);
          updatedPermission.report.push(shop.id);
          updatedPermission.takeaway.push(shop.id);
        }
        if (shop.roles?.includes("shopStaff")) {
          updatedPermission.product.redeem.push(shop.id);
          updatedPermission.product.distribute.push(shop.id);
          updatedPermission.estamp.distribute.push(shop.id);
        }
      }
    });

    // Ensure takeaway and report only contain "all" if present
    if (updatedPermission.takeaway.includes("all")) {
      updatedPermission.takeaway = ["all"];
    }

    if (updatedPermission.report.includes("all")) {
      updatedPermission.report = ["all"];
    }

    // limit permission for shopStaff
    if (!shopArray.some((shop) => ["all", "marketing", "shopManager"].includes(shop?.roles?.[0]))) {
      updatedPermission.shop = false;
      updatedPermission.report = []; // Set to []
      updatedPermission.takeaway = []; // Set to []
    }

    log.debug("toPermission", { updatedPermission });
    return updatedPermission;
  };

  const tableData = useMemo(() => {
    return convertPermissionToArray(permission, shopsCache?.["undefined"]);
  }, [permission]);

  // log.debug("tableData", tableData);

  const [selectedItems, setSelectedItems] = useState([]);
  const [isShowShopPermissionDrawer, setIsShowShopPermissionDrawer] = useState(false);
  const [isShowShopSelectionDrawer, setIsShowShopSelectionDrawer] = useState(false);

  const columns = [
    {
      title: "Shop",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <Meta
          // image={
          //   record?.id == "all" ? null : (
          //     <img
          //       style={{ width: "3rem", height: "3rem", objectFit: "cover", marginRight: "0.5rem" }}
          //       src={metaData.blobPreURL + "clientUpload-" + record?.brand?.logo + ".jpeg" + metaData.blobPostURL}
          //     />
          //   )
          // }
          title={<Title level={5}>{record?.name}</Title>}
          description={c.getDisplayAddress(record?.address)}
        />
      ),
      sorter: (a, b) => (a?.name).localeCompare(b?.name),
    },
    // {
    //   title: "Permission",
    //   dataIndex: "permission",
    //   key: "permission",
    //   render: (text, record) => {
    //     const _per = record?.permission || {}; // Fallback to an empty object if undefined
    //     return <Row>{Object.values(_per).filter(Boolean).length}</Row>;
    //   },
    // },
    {
      title: "Permission",
      dataIndex: "permission",
      key: "permission",
      render: (text, record) => {
        // const mapping = {
        //   marketing: { label: "Marketing", color: "blue", icon: <QrcodeOutlined /> },
        //   shopManager: { label: "Shop Manager", color: "purple", icon: <QrcodeOutlined /> },
        //   shopStaff: { label: "Shop Staff", color: "orange", icon: <AreaChartOutlined /> },
        // };

        // return record?.roles.map((role, i) => (
        //   <Tag key={i} color={mapping[role].color}>
        //     {mapping[role].label}
        //   </Tag>
        // ));
        return record?.roles?.[0] == "marketing" ? (
          <Space>
            <PermissionPopover id="report" />
            <PermissionPopover id="shop" />
            <PermissionPopover id="product" />
            <PermissionPopover id="eStamp" />
            {company?.permission?.isTakeawayEnabled ? <PermissionPopover id="takeaway" /> : null}
            <PermissionPopover id="promotionCode" />
            <PermissionPopover id="welcome" />
            <PermissionPopover id="ad" />
            <PermissionPopover id="pushNotification" />
          </Space>
        ) : record?.roles?.[0] == "shopManager" ? (
          <Space>
            <PermissionPopover id="report" />
            {company?.permission?.isTakeawayEnabled ? <PermissionPopover id="takeaway" /> : null}
            <PermissionPopover id="redeem" />
            <PermissionPopover id="eStampQR" />
            {company?.permission?.isTakeawayEnabled ? <PermissionPopover id="takeawayQR" /> : null}
          </Space>
        ) : (
          <Space>
            <PermissionPopover id="redeem" />
            <PermissionPopover id="eStampQR" />
            {company?.permission?.isTakeawayEnabled ? <PermissionPopover id="takeawayQR" /> : null}
          </Space>
        );
      },
    },
    {
      title: "",
      key: "action",
      width: 200,
      render: (text, record) =>
        readOnly ? null : (
          <Row justify="end">
            <Button
              size="large"
              variant="outlined"
              style={{ marginRight: "3rem" }}
              onClick={() => {
                //delete row from table
                onChange({
                  permission: convertArrayToPermission(tableData?.filter((shop) => shop.id !== record?.id)),
                });
              }}
              icon={<DeleteOutlined style={{ color: "var(--ion-color-red-600)" }} />}
            />
          </Row>
        ),
    },
  ];

  const options = useMemo(() => {
    const filteredOptions =
      shopsCache?.["undefined"]
        ?.filter((e) => !tableData?.some((shop) => shop.id === e.id)) // Check if any shop in tableData has the same ID
        .map((e) => ({
          value: e.id,
          name: e.name,
          label: (
            <Space>
              <ShopLogo
                style={{ width: 40, height: 40, objectFit: "cover", marginRight: "0.5rem" }}
                logo={e?.logo}
                brandLogo={e?.brand?.logo}
              />
              {e.name}
            </Space>
          ),
        })) || [];

    return filteredOptions;
  }, [shopsCache, tableData]);

  const mergeShopData = (tableData, newData) => {
    const mergedData = [...tableData];

    newData.forEach((newShop) => {
      const existingIndex = mergedData.findIndex((shop) => shop.id === newShop.id);

      if (existingIndex > -1) {
        // If the shop with the same ID exists, overwrite its permissions
        mergedData[existingIndex] = newShop;
      } else {
        // If the shop is new, add it to the array
        mergedData.push(newShop);
      }
    });

    return mergedData;
  };

  const getShopsData = async () => {
    if (!user?.companyId) {
      return;
    }

    let result;
    try {
      // setIsFetchingShops(true);
      result = await getShops();
      log.debug(result);

      //add key to disable antd table warning
      // setData(
      //   result.map((e, i) => {
      //     return { ...e, key: i };
      //   })
      // );
    } catch (error) {
      notification.error({
        message: (
          <Title style={{ margin: 0 }} level={4}>
            Error
          </Title>
        ),
        description: <Text strong>Please refresh this page</Text>,
      });
    } finally {
      // setIsFetchingShops(false);
    }
  };

  useEffect(() => {
    getShopsData();
  }, []);

  return (
    <>
      <ShopSelectDrawer
        isShowShopSelectionDrawer={isShowShopSelectionDrawer}
        selectedItems={selectedItems}
        tableData={tableData}
        options={options}
        onClose={() => {
          setIsShowShopSelectionDrawer(false);
        }}
        onConfirm={setSelectedItems}
      />

      <ShopPermissionDrawer
        isShowShopPermissionDrawer={isShowShopPermissionDrawer}
        selectedItems={selectedItems}
        tableData={tableData}
        setIsShowShopSelectionDrawer={setIsShowShopSelectionDrawer}
        onClose={() => {
          // Clear selected items
          setSelectedItems([]);

          //
          setIsShowShopPermissionDrawer(false);
        }}
        onConfirm={(value) => {
          log.debug(value, selectedItems);

          //store user selected role with shopIds

          let data = [];

          // Case where "marketing" is true
          if (value.marketing) {
            data.push({
              id: "all",
              name: "All Shops",
              address: "placeholder",
              key: "all",
              roles: ["all"],
            });
          } else {
            // Get the selected shops from the cache
            const filteredShops = shopsCache["undefined"]
              .filter((e) => selectedItems.includes(e.id))
              .map((shop, i) => {
                let role = value.shopManager ? "shopManager" : "shopStaff";
                return {
                  ...shop,
                  key: i,
                  roles: [role], // Only one role will be true
                };
              });

            log.debug("filteredShops", filteredShops);

            // Append filtered shops to the data array
            data = [...data, ...filteredShops];
          }

          // Add row into table
          onChange({ permission: convertArrayToPermission(mergeShopData(tableData, data)) });

          // Clear selected items
          setSelectedItems([]);

          //
          setIsShowShopPermissionDrawer(false);
        }}
      />

      {readOnly ? null : (
        <>
          <Button
            type="primary"
            size="large"
            disabled={
              (tableData?.[0]?.id == "all" && tableData?.[0]?.permission?.shopStaff) ||
              (tableData?.[0]?.id == "all" && options.length == 0)
            }
            style={{ marginBottom: "1rem", marginRight: "2rem" }}
            onClick={() => {
              setIsShowShopPermissionDrawer(true);
            }}
          >
            Add
          </Button>

          {tableData.length > 0 ? (
            <Button
              variant="link"
              color="primary"
              size="large"
              style={{ marginBottom: "1rem" }}
              onClick={() => {
                //clear table
                onChange({
                  permission: permissionMetaData.default.no,
                });
              }}
            >
              clear all
            </Button>
          ) : null}
        </>
      )}

      <Table className="white-table-header" sticky columns={columns} dataSource={tableData} pagination={false} />

      <Row style={{ marginBottom: "5rem" }}></Row>
    </>
  );
};

export default UserPermissionSelector;
